<template>
  <v-skeleton-loader
    v-if="loading"
    class="m-auto"
    type="table"
    transition="scale-transition"
  />
  <VTableRemote
    v-else
    :iconHeader="hasPermission('workOrders.types.add') ? 'add' : null"
    :items="displayedTypes"
    isSearchable
    isCustomIcon
    hideDefaultFooter
    :isInfinitScroll="true"
    :headers="tableHeaders"
    :sortableColumns="sortableColumns"
    :itemActions="actions"
    :title="$t('workOrders.types.pageName')"
    :centerColumns="['action']"
    :notSortableColumns="['action']"
    :pagination="pagination"
    @iconAction="onClickAction"
    @click-icon-header="onClickAdd"
    @sendQueryParams="onQueryParams"
  >
    <template #withInfiniteScroll>
      <div v-intersect="endIntersect" />
    </template>
  </VTableRemote>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import WorkOrderTypeForm from '@/views/admin/configuration/workOrderTypes/WorkOrderTypeForm.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import perpage from '@/constants/pagination';

export default {
    components: {

        VTableRemote,
    },
    data: () => ({
        types: [],
        pagination: {},
        queryParams: {
            page: 1,
            perpage,
            search: '',
            pagination: true,
        },
        loading: false,
    }),
    computed: {
        ...mapGetters([
            'lang',
            'hasPermission',
            'teamMainLang',

        ]),
        tableHeaders() {
            return [{
                text: this.$t('forms.labels.name'),
                value: 'name',
            }];
        },
        displayedTypes() {
            return this.types.map(type => ({
                id: type.id,
                name: type.name[this.lang] || type.name[this.teamMainLang],
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('workOrders.types.edit')) {
                actions.push({
                    name: this.$t('forms.labels.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('workOrders.types.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
        sortableColumns() {
            return this.tableHeaders.map(column => this.$options.filters.lowercase(column.value));
        },
    },
    async created() {
        this.loading = true;
        await this.getTypes();
        this.loading = false;
    },
    methods: {
        async getTypes() {
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            const { data } = await this.$rq.getWorkOrderTypes(cleanQueryParams);
            this.types = [...this.types, ...data.alltypes];
            this.pagination = data.pagination;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('workOrders.types.add'),
                showLangs: true,
                component: Vue.component('WorkOrderTypeForm', WorkOrderTypeForm),
                onConfirm: requestPayload => this.addType(requestPayload),
            });
        },
        async onClickAction(item, action) {
            if (item === 'add') {
                this.onClickAdd();
                return;
            }
            if (action.mode === 'edit') {
                const { data } = await this.$rq.getWorkOrderType(item.id);
                this.$root.$emit('showModal', {
                    title: this.$t('workOrders.types.update'),
                    component: Vue.component('WorkOrderTypeForm', WorkOrderTypeForm),
                    showLangs: true,
                    payload: { name: cloneDeep(data.name), roles: data.roles, isEdition: true },
                    onConfirm: requestPayload => this.updateType(item.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('workOrders.types.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('workOrders.types.confirmRemove') },
                    onConfirm: () => this.removeType(item),
                    isForm: false,
                });
            }
        },
        async addType(requestPayload) {
            await this.$rq.addWorkOrderType(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('workOrders.types.add', { name: requestPayload.name[this.lang] }),
            });
            await this.onQueryParams({});
        },
        async updateType(id, payload) {
            if (!payload) return;
            const { name, roles } = payload;
            const cleanRoles = roles?.map(role => {
                if (typeof role === 'string') return role;
                return role.key;
            });
            await this.$rq.updateWorkOrderType(id, { name, roles: cleanRoles });
            this.$toasted.global.toast_success({
                message: this.$t('workOrders.types.updated', { name: name[this.lang] }),
            });
            await this.onQueryParams({});
        },
        async removeType(type) {
            await this.$rq.removeWorkOrderType(type.id);
            this.$toasted.global.toast_success({
                message: this.$t('workOrders.types.removed', { name: type.name[this.lang] }),
            });
            await this.onQueryParams({});
        },
        onQueryParams(queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
                search: queryParams?.search || '',
                page: 1,
            };
            this.pagination = {};
            this.types = [];
            this.getTypes();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getTypes();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
